import { useEffect, type ComponentType } from 'react';
import { CheckIcon, LightbulbIcon, WarningIcon, XMarkIcon } from './Icons';
import { useI18n } from '../../i18n';

export type ToastKind = 'success' | 'warning' | 'error' | 'info';

interface ToastProps {
  message: string;
  onDismiss: () => void;
  autoHide?: number; // ms, default 3000
  /** #666 — The toast stays until the user closes it: no auto-hide, and a
   *  close button. A tap on the toast closes it too, as on every toast. */
  untilClosed?: boolean;
  /** The kind decides the colour. Mandatory (2026-09-19): the component no longer guesses from words. */
  kind: ToastKind;
}

// One colour per kind, and every kind has one (2026-09-19): ten call sites
// passed 'info', which was not a kind, so those toasts rendered with no
// background — three of them error paths. Warning is orange (operator's
// word), not amber.
const KIND_CLASS: Record<ToastKind, string> = {
  success: 'bg-green-500 text-white',
  warning: 'bg-orange-500 text-white',
  error:   'bg-red-500 text-white',
  info:    'bg-blue-500 text-white',
};

// #664 (F67) — the inline notice has the four kinds of the toast and a gray
// one. `Notice.tsx` is the only reader: the box and the text colour of each
// tone are decided here, once. The warning box is amber, not the orange of
// the toast, because a pale orange box reads as an error beside a red one.
export type NoticeTone = ToastKind | 'neutral';

export const NOTICE_CLS: Record<NoticeTone, { box: string; text: string }> = {
  error:   { box: 'bg-red-50 border border-red-200',     text: 'text-red-700' },
  warning: { box: 'bg-amber-50 border border-amber-200', text: 'text-amber-800' },
  success: { box: 'bg-green-50 border border-green-200', text: 'text-green-700' },
  info:    { box: 'bg-blue-50 border border-blue-200',   text: 'text-blue-800' },
  neutral: { box: 'bg-gray-50 border border-gray-200',   text: 'text-gray-700' },
};

// #603 — one icon for each kind, an SVG of the app: a toast text holds no
// ✓ and no ⚠ of its own.
export const KIND_ICON: Record<ToastKind, ComponentType<{ className?: string }>> = {
  success: CheckIcon,
  warning: WarningIcon,
  error:   XMarkIcon,
  info:    LightbulbIcon,
};

export default function Toast({ message, onDismiss, autoHide = 3000, untilClosed = false, kind }: ToastProps) {
  const { t } = useI18n();
  // `kind` is a dependency too (#557 review): a toast that waited shows when
  // the toast of a higher rank hides, and it always has a different kind.
  // If the text is the same, only the kind starts the timer again. Without
  // it, that toast does not hide.
  useEffect(() => {
    if (!message || untilClosed) return;
    const timer = setTimeout(onDismiss, autoHide);
    return () => clearTimeout(timer);
  }, [message, kind, autoHide, untilClosed, onDismiss]);

  if (!message) return null;

  const resolvedKind: ToastKind = kind;
  const Icon = KIND_ICON[resolvedKind];

  // On a phone the toast floats 3.5rem below the top edge, or lower when
  // the device reports a larger safe area: at the top edge, the camera
  // island of an iPhone covered the text (operator, 2026-09-25).
  // `env(safe-area-inset-top)` is 0 without `viewport-fit=cover`, so the
  // 3.5rem floor is what clears the island.
  return (
    <div
      role="status"
      data-toast-kind={resolvedKind}
      className={`fixed top-[max(3.5rem,env(safe-area-inset-top))] left-3 right-3 z-60 rounded-lg px-4 py-3 text-center text-sm font-bold shadow-lg cursor-pointer
        md:left-auto md:right-4 md:top-4 md:max-w-sm
        ${KIND_CLASS[resolvedKind]}`}
      onClick={onDismiss}
    >
      <span className="inline-flex items-start justify-center gap-2">
        <Icon className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{message}</span>
        {/* #666 — The click of the button reaches the click of the toast, which closes it. The
            negative margins keep the 36 px target from making the toast taller. */}
        {untilClosed && (
          <button type="button" data-testid="toast-close" title={t.common.close} aria-label={t.common.close}
            className="shrink-0 -my-2 -mr-2 inline-flex min-h-9 min-w-9 items-center justify-center rounded hover:bg-white/20 cursor-pointer">
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </span>
    </div>
  );
}
